<template>
  <div class="corpo-pagina">
    <div class="row row-text controls">
      <div class="col">
        <select v-model="tableShape" class="form-control" @change="seats = null">
          <option value="circle">Tavolo Rotondo</option>
          <option value="rect">Tavolo Rettangolare</option>
        </select>
      </div>
      <div class="col">
        <select v-model="seats" @change="creaTavolo" class="form-control"
          :disabled="!tableShape || tableShape === null">
          <option :value="null" disabled>Seleziona Posti</option>
          <option v-for="n in 10" :key="n" :value="n">{{ n }} Posti</option>
        </select>
      </div>
      <div class="col">
        <button @click="resetAll" class="form-control reset-btn">Reset Tavoli/Ospiti</button>
        <button @click="salvaStage" class="form-control save-btn">Salva Disposizione</button>
      </div>
    </div>

    <div class="row table-manager-container">

      <div class="col guest-list-sidebar">
        <h3>Ospiti da Assegnare ({{ guestsToAssign.length }})</h3>

        <input
            v-model="searchQuery"
            type="text"
            placeholder="Cerca per Nome o Cognome..."
            class="form-control"
            style="margin-bottom: 10px;"
        >

        <p v-if="!guests.length">Caricamento ospiti...</p>

        <div v-for="guestSlot in guestsToAssign" :key="guestSlot.uniqueId"
             class="guest-group-entry">

            <div
                :id="'guest-figure-sidebar-' + guestSlot.uniqueId"
                class="guest-figure"
                :class="{
                    'main-guest': guestSlot.isMainGuest,
                    'dimension-accompanying': !guestSlot.isMainGuest
                }"
                :style="{ backgroundColor: getGuestColor(guestSlot.uid) }"
                @mousedown="dragStartGroup(guestSlot.groupObject)"
                :data-group-uid="guestSlot.uid">

                <span>{{ guestSlot.displayName }}</span>

                <span v-if="guestSlot.groupObject.accompanying_persons > 0 && guestSlot.isMainGuest"
                      class="accompanying-count">
                    +{{ guestSlot.groupObject.accompanying_persons }}
                </span>
            </div>
        </div>
        <p v-if="searchQuery && guestsToAssign.length === 0" style="color:#999; font-style:italic;">Nessun ospite trovato per "{{ searchQuery }}".</p>
      </div>

      <div id="container-wrapper"
        style="overflow: scroll;"
        class="col resizable-container">
        <div id="container" class="droptarget"></div>
        <div class="resizable-handle" @mousedown="startStageResize"></div>
      </div>
    </div>
  </div>
</template>

<script>
import Konva from 'konva'
// Importa lo store usato per salvare i dati della stanza, ipotizzato 'konovaschema' o 'store' generico.
import konovaschema from '@/store/konovaschema.js' // Se usi uno store separato

export default {
  name: 'TableManager',
  data () {
    return {
      stage: null,

      stageWidth: 800,
      stageHeight: 600,
      minStageSize: 300,

      isResizing: false,
      initialMouseX: 0,
      initialMouseY: 0,
      initialStageWidth: 0,
      initialStageHeight: 0,

      searchQuery: '',

      tableShape: 'circle',
      seats: null,

      baseTableRadius: 50,
      baseRectWidth: 100,
      rectHeight: 70,
      minSpacePerPerson: 60,
      seatVisualRadius: 20,
      seatDistancePadding: 25,

      GUEST_KONVA_COLOR: '#ff4d4d',

      guests: [],
      guestColorMap: {},
      seatOccupancy: {},
      draggedGuestUid: null,
      oldSeatId: null,

      tableLayer: null,
      figureLayer: null,
      transformer: null,
      tableCounter: 0,

      roomData: null, // Dati della stanza (contiene 'img' o 'color')

      // Mappa delle immagini disponibili con percorsi risolti da Webpack
      imageOptions: [
        { value: 'modern', path: require('@/assets/img/table/modern.png') },
        { value: 'liberty', path: require('@/assets/img/table/liberty.png') },
        { value: 'principesco', path: require('@/assets/img/table/principesco.png') },
        { value: 'elegante', path: require('@/assets/img/table/rosomi.png') },
        { value: 'natale', path: require('@/assets/img/table/natale.png') }
      ]
    }
  },

  computed: {
    // Getter Vuex per lo schema Konva salvato
    konvaSchemaFromStore () {
      // Usa il tuo store specifico per lo schema Konva
      return konovaschema.getters.getKonvaSchema
    },

    // Getter Vuex per i dati completi della stanza
    roomDataFromStore () {
      // Usa il tuo store specifico per i dati della stanza (salvati da RoomCreateView)
      return konovaschema.getters.getRoomData
    },

    // Calcola dinamicamente il CSS dello sfondo per il div #container-wrapper
    dynamicBackgroundStyle () {
      const room = this.roomData

      if (!room) {
        return { backgroundColor: '#f0f0f0' } // Fallback
      }

      // Se è stato scelto un colore
      if (room.color) {
        return {
          backgroundColor: room.color,
          backgroundImage: 'none'
        }
      }

      // Se è stata scelta un'immagine (tramite il suo value breve, es. 'modern')
      const imgValue = room.img
      const selectedOption = this.imageOptions.find(opt => opt.value === imgValue)

      if (selectedOption) {
        // Utilizza il percorso risolto da require()
        return {
          backgroundImage: `url(${selectedOption.path})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: 'transparent'
        }
      }

      return { backgroundColor: '#f0f0f0' } // Fallback
    },

    // ... (altre computed properties come guestsToAssign, maxTableDimension)
    guestsToAssign () {
      // ... (la logica rimane la stessa)
      const allSeats = []
      const searchTerm = this.searchQuery.toLowerCase().trim()

      this.guests
        .map(g => ({
          ...g,
          groupId: 'guest-figure-' + g.uid,
          totalSeatsNeeded: 1 + (g.accompanying_persons || 0)
        }))
        .filter(g => {
          const isAssigned = Object.values(this.seatOccupancy).includes(g.groupId)
          if (isAssigned) return false

          if (searchTerm) {
            const fullName = `${g.name} ${g.surname}`.toLowerCase()
            return fullName.includes(searchTerm)
          }
          return true
        })
        .forEach(group => {
          const groupNamePrefix = group.surname.substring(0, 5).toUpperCase()

          allSeats.push({
            uniqueId: `${group.uid}-main`,
            uid: group.uid,
            groupId: group.groupId,
            displayName: `${group.name} ${group.surname.charAt(0)}.`,
            isMainGuest: true,
            groupObject: group
          })

          for (let i = 1; i <= group.accompanying_persons; i++) {
            allSeats.push({
              uniqueId: `${group.uid}-acc-${i}`,
              uid: group.uid,
              groupId: group.groupId,
              displayName: `Acc. ${groupNamePrefix} (${i})`,
              isMainGuest: false,
              groupObject: group
            })
          }
        })
      return allSeats
    },

    maxTableDimension () {
      const maxCircleRadius = this.baseTableRadius + this.seatVisualRadius + this.seatDistancePadding
      const maxCircle = maxCircleRadius * 2

      const seatsForLongSidesTotal = Math.max(0, 10 - 2)
      const maxSeatsPerLongSide = Math.floor(seatsForLongSidesTotal / 2)
      let maxRectWidth = (maxSeatsPerLongSide + 1) * this.minSpacePerPerson
      maxRectWidth = Math.max(maxRectWidth, this.baseRectWidth)
      const maxRectFullWidth = maxRectWidth + 2 * (this.seatVisualRadius + this.seatDistancePadding)

      const maxRectFullHeight = this.rectHeight + 2 * (this.seatVisualRadius + this.seatDistancePadding)

      return Math.max(maxCircle, maxRectFullWidth, maxRectFullHeight)
    }

  },

  mounted () {
    this.loadGuestsFromLocalStorage()
    // *** NUOVO: Carica i dati della stanza da Vuex prima di caricare lo Stage ***
    this.loadRoomData()
    this.loadStageFromVuex()

    document.addEventListener('mousemove', this.handleStageResize)
    document.addEventListener('mouseup', this.stopStageResize)
  },

  beforeUnmount () {
    document.removeEventListener('mousemove', this.handleStageResize)
    document.removeEventListener('mouseup', this.stopStageResize)
  },

  methods: {
    // *** NUOVO METODO: Carica i dati della stanza ***
    loadRoomData () {
      this.roomData = this.roomDataFromStore
    },
    // --- Metodi di Caricamento Stage ---

    /**
     * Tenta di caricare lo Stage Konva dallo schema JSON salvato in Vuex.
     * Se fallisce o non esiste, inizializza uno Stage vuoto.
     */
    loadStageFromVuex () {
      const konvaSchema = this.konvaSchemaFromStore

      if (konvaSchema) {
        try {
          this.stage = Konva.Node.create(konvaSchema, 'container')
          this.tableLayer = this.stage.findOne('.TableLayer')
          this.figureLayer = this.stage.findOne('.FigureLayer')
          this.transformer = this.tableLayer.findOne('Transformer')
          this.stageWidth = this.stage.width()
          this.stageHeight = this.stage.height()
          this.recalculateStateFromStage()
          this.stage.off('click tap').on('click tap', this.handleStageClick)
          this.stage.draw()
        } catch (e) {
          console.error('Errore nel caricamento dello Stage da Vuex:', e)
          this.initializeStage() // Fallback
        }
      } else {
        this.initializeStage()
      }
    },

    /**
     * Ricostruisce lo stato interno del componente (contatore tavoli, occupancy)
     * leggendo gli elementi dal Konva Stage appena caricato.
     */
    recalculateStateFromStage () {
      this.seatOccupancy = {}
      let maxTableNumber = 0

      this.tableLayer.find('.gruppo_tavolo').forEach(group => {
        const tableId = group.id()
        const match = tableId.match(/table-group-(\d+)/)
        if (match) {
          maxTableNumber = Math.max(maxTableNumber, parseInt(match[1]))
        }

        group.on('transform', () => this.handleTableTransform(group))

        group.find('.seat').forEach(seat => {
          if (seat.fill() === this.GUEST_KONVA_COLOR) {
            const seatId = seat.id()

            // Cerca la figura ospite permanente vicina
            const foundFigures = this.figureLayer.find(f => {
              const seatAbsPos = seat.getAbsolutePosition()
              const figureAbsPos = f.getAbsolutePosition()
              return Math.abs(seatAbsPos.x - figureAbsPos.x) < 5 && Math.abs(seatAbsPos.y - figureAbsPos.y) < 5
            })

            const figureId = foundFigures[0]?.id()

            if (figureId) {
              this.$set(this.seatOccupancy, seatId, figureId)
            } else {
              // Se non troviamo la figura, non sappiamo chi occupa il posto, lo liberiamo.
              seat.fill('#DEDEDE')
              seat.stroke('#999999')
            }
          }
        })
      })

      this.tableCounter = maxTableNumber
      this.updateTableDragBounds()

      this.guests.forEach(g => {
        this.restoreSidebarVisibility(g.uid)
      })
    },

    // --- Metodi resize Sala ---
    startStageResize (e) {
      this.isResizing = true
      this.initialMouseX = e.clientX
      this.initialMouseY = e.clientY
      this.initialStageWidth = this.stageWidth
      this.initialStageHeight = this.stageHeight
      e.preventDefault()
    },

    handleStageResize (e) {
      if (!this.isResizing) return

      const dx = e.clientX - this.initialMouseX
      const dy = e.clientY - this.initialMouseY

      const newWidth = Math.max(this.minStageSize, this.initialStageWidth + dx)
      const newHeight = Math.max(this.minStageSize, this.initialStageHeight + dy)

      this.stageWidth = newWidth
      this.stageHeight = newHeight

      this.updateKonvaStageDimensions()
    },

    stopStageResize () {
      this.isResizing = false
    },

    updateKonvaStageDimensions () {
      if (this.stage) {
        this.stage.width(this.stageWidth)
        this.stage.height(this.stageHeight)

        this.updateTableDragBounds()

        const borderRect = this.stage.findOne('#stage-border')
        if (borderRect) {
          borderRect.width(this.stageWidth)
          borderRect.height(this.stageHeight)
        }
        this.stage.draw()
      }
    },

    updateTableDragBounds () {
      const halfMaxDim = this.maxTableDimension / 2

      const minX = halfMaxDim
      const minY = halfMaxDim
      const maxX = this.stageWidth - halfMaxDim
      const maxY = this.stageHeight - halfMaxDim

      this.tableLayer?.find('.gruppo_tavolo').forEach(group => {
        group.setAttr('dragBoundFunc', (pos) => {
          return {
            x: Math.min(Math.max(pos.x, minX), maxX),
            y: Math.min(Math.max(pos.y, minY), maxY)
          }
        })
        group.x(Math.min(Math.max(group.x(), minX), maxX))
        group.y(Math.min(Math.max(group.y(), minY), maxY))
      })
      this.tableLayer?.draw()
    },

    // --- Logica Ospiti ---
    loadGuestsFromLocalStorage () {
      try {
        const settingsString = localStorage.getItem('SettingsStore')
        if (settingsString) {
          const settings = JSON.parse(settingsString)
          const guestsArray = settings.eventModel && settings.eventModel.guests ? settings.eventModel.guests : []
          if (guestsArray.length > 0) {
            this.guests = guestsArray.map((g) => ({
              ...g,
              accompanying_persons: g.accompanying_persons || 0
            }))
            this.guests.forEach(g => {
              this.$set(this.guestColorMap, g.uid, this.getRandomColor())
            })
          }
        }
      } catch (e) {
        console.error('Errore durante il caricamento degli ospiti:', e)
      }
    },

    getRandomColor () {
      const letters = '0123456789ABCDEF'
      let color = '#'
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
      }
      return color
    },

    getGuestColor (uid) {
      return this.guestColorMap[uid] || '#000000'
    },

    // --- Logica Konva Stage/Transformer (Inizializzazione VUOTA) ---
    initializeStage () {
      this.stage = new Konva.Stage({
        container: 'container',
        width: this.stageWidth,
        height: this.stageHeight
      })

      const borderLayer = new Konva.Layer()
      const borderRect = new Konva.Rect({
        x: 0,
        y: 0,
        width: this.stageWidth,
        height: this.stageHeight,
        stroke: 'black',
        strokeWidth: 2,
        id: 'stage-border'
      })
      borderLayer.add(borderRect)
      this.stage.add(borderLayer)

      this.tableLayer = new Konva.Layer({ name: 'TableLayer' })
      this.stage.add(this.tableLayer)

      this.figureLayer = new Konva.Layer({ name: 'FigureLayer' })
      this.stage.add(this.figureLayer)

      this.transformer = new Konva.Transformer({
        rotateEnabled: true,
        borderStroke: 'blue',
        anchorFill: 'blue',
        anchorStroke: 'blue',
        enabledAnchors: []
      })
      this.tableLayer.add(this.transformer)

      this.updateTableDragBounds()
      this.stage.on('click tap', this.handleStageClick)
    },

    handleStageClick (e) {
      const target = e.target

      if (target === this.stage || target.name() === 'seat' || target.name() === 'guest-figure' || target.id() === 'stage-border') {
        this.transformer.nodes([])
        this.tableLayer.draw()
        return
      }

      const group = target.getParent()
      if (group && group.name() === 'gruppo_tavolo') {
        this.transformer.nodes([group])
        this.tableLayer.draw()
      } else {
        this.transformer.nodes([])
        this.tableLayer.draw()
      }
    },

    // --- Logica Tavoli e Posti ---
    creaTavolo () {
      const numSeats = parseInt(this.seats)
      this.tableCounter++
      const newTableId = `table-group-${this.tableCounter}`

      const xPos = 300 + (this.tableCounter % 3) * 250
      const yPos = 150 + Math.floor((this.tableCounter - 1) / 3) * 250

      const group = new Konva.Group({
        draggable: true,
        name: 'gruppo_tavolo',
        id: newTableId,
        x: xPos,
        y: yPos,
        scaleX: 1,
        scaleY: 1,
        originalNumSeats: numSeats,
        tableShape: this.tableShape,

        dragBoundFunc: (pos) => {
          const halfMaxDim = this.maxTableDimension / 2
          const minX = halfMaxDim
          const minY = halfMaxDim
          const maxX = this.stageWidth - halfMaxDim
          const maxY = this.stageHeight - halfMaxDim

          return {
            x: Math.min(Math.max(pos.x, minX), maxX),
            y: Math.min(Math.max(pos.y, minY), maxY)
          }
        }
      })

      let tableElement

      if (this.tableShape === 'circle') {
        const tableRadius = this.baseTableRadius
        tableElement = new Konva.Circle({
          name: 'table',
          x: 0,
          y: 0,
          radius: tableRadius,
          fill: '#F5DEB3',
          stroke: '#696969',
          strokeWidth: 2,
          tableRadius: tableRadius
        })
        group.add(tableElement)
      } else if (this.tableShape === 'rect') {
        const seatsPerLongSide = Math.floor(Math.max(0, numSeats - 2) / 2)
        let w = (seatsPerLongSide + 1) * this.minSpacePerPerson
        w = Math.max(w, this.baseRectWidth)

        tableElement = new Konva.Rect({
          name: 'table',
          x: -w / 2,
          y: -this.rectHeight / 2,
          width: w,
          height: this.rectHeight,
          cornerRadius: 10,
          fill: '#8B4513',
          stroke: '#696969',
          strokeWidth: 2,
          tableWidth: w,
          tableHeight: this.rectHeight
        })
        group.add(tableElement)
      }

      this.generateSeats(group, numSeats)

      this.tableLayer.add(group)
      this.transformer.nodes([group])
      this.tableLayer.draw()
      this.seats = null
    },

    handleTableTransform (tableGroup) {
      this.repositionOccupiedGuests(tableGroup)
      this.tableLayer.draw()
    },

    generateSeats (tableGroup, numSeats) {
      const newTableId = tableGroup.id()
      const tableElement = tableGroup.findOne('.table')
      const shape = tableGroup.getAttr('tableShape')

      tableGroup.find('.seat').forEach(seat => {
        if (!this.seatOccupancy[seat.id()]) {
          seat.destroy()
        }
      })

      let seatPositions = []
      const scaleX = 1
      const scaleY = 1

      if (shape === 'circle') {
        const tableRadius = tableElement.radius()
        const seatRadius = this.seatVisualRadius
        const distRadiusX = tableRadius + seatRadius + this.seatDistancePadding
        const distRadiusY = tableRadius + seatRadius + this.seatDistancePadding
        const angle = 360 / numSeats
        const radmult = Math.PI / 180

        for (let i = 0; i < numSeats; i++) {
          const posAngle = angle * i
          const x = distRadiusX * Math.cos(posAngle * radmult)
          const y = distRadiusY * Math.sin(posAngle * radmult)
          seatPositions.push({ x: x / scaleX, y: y / scaleY })
        }
      } else if (shape === 'rect') {
        const w = tableElement.width()
        const h = tableElement.height()
        const padding = 20

        const seatsOnShortSides = (numSeats < 2) ? numSeats : 2
        const seatsForLongSidesTotal = Math.max(0, numSeats - seatsOnShortSides)
        const seatsPerLongSide = Math.floor(seatsForLongSidesTotal / 2)
        const spacingX = w / (seatsPerLongSide + 1)

        // Sedie lato lungo (sopra)
        for (let i = 1; i <= seatsPerLongSide; i++) {
          seatPositions.push({ x: (-w / 2) + spacingX * i, y: (-h / 2) - padding })
        }
        // Sedie lato lungo (sotto)
        for (let i = 1; i <= seatsPerLongSide; i++) {
          seatPositions.push({ x: (-w / 2) + spacingX * i, y: (h / 2) + padding })
        }
        // Sedie lato corto (sinistra)
        if (seatsOnShortSides >= 1) {
          seatPositions.push({ x: (-w / 2) - padding, y: 0 })
        }
        // Sedie lato corto (destra)
        if (seatsOnShortSides === 2) {
          seatPositions.push({ x: (w / 2) + padding, y: 0 })
        }
        // 1 posto
        if (numSeats === 1) {
          seatPositions = [{ x: 0, y: (-h / 2) - padding }]
        }
      }

      seatPositions.forEach((pos, index) => {
        const seatId = `${newTableId}-seat-${index}`
        const figureId = this.seatOccupancy[seatId]
        const isOccupied = !!figureId

        let seat = tableGroup.findOne(`#${seatId}`)

        if (!seat) {
          seat = new Konva.Circle({
            name: 'seat',
            id: seatId,
            fill: isOccupied ? this.GUEST_KONVA_COLOR : '#DEDEDE',
            stroke: isOccupied ? '#4d94ff' : '#999999',
            strokeWidth: 2,
            isOccupied: isOccupied
          })
          tableGroup.add(seat)
          seat.moveToBottom()
        }

        seat.x(pos.x)
        seat.y(pos.y)
        seat.radius(this.seatVisualRadius)

        if (isOccupied) {
          seat.fill(this.GUEST_KONVA_COLOR)
          seat.stroke('#4d94ff')
        } else {
          seat.fill('#DEDEDE')
          seat.stroke('#999999')
        }
      })

      this.tableLayer.draw()
    },

    repositionOccupiedGuests (tableGroup) {
      tableGroup.find('.seat').forEach(seat => {
        const seatId = seat.id()
        const figureId = this.seatOccupancy[seatId]

        if (figureId) {
          const guestFigure = this.figureLayer.findOne('#' + figureId)

          if (guestFigure) {
            const seatAbsPos = tableGroup.getAbsoluteTransform().point({
              x: seat.x(),
              y: seat.y()
            })

            guestFigure.x(seatAbsPos.x)
            guestFigure.y(seatAbsPos.y)
            guestFigure.radius(this.seatVisualRadius)
          }
        }
      })
      this.figureLayer.draw()
      this.tableLayer.draw()
    },

    // --- Logica Drag & Drop Gruppi ---

    dragStartGroup (guest) {
      this.draggedGuestUid = 'guest-figure-' + guest.uid
      const totalSeatsNeeded = 1 + (guest.accompanying_persons || 0)

      const groupElements = document.querySelectorAll(`.guest-group-entry div.guest-figure[data-group-uid="${guest.uid}"]`)
      groupElements.forEach(el => {
        el.style.opacity = '0.4'
      })

      const oldSeatIds = Object.keys(this.seatOccupancy).filter(seatId => this.seatOccupancy[seatId] === this.draggedGuestUid)

      for (const oldId of oldSeatIds) {
        const oldSeat = this.tableLayer.findOne(`#${oldId}`)
        if (oldSeat) {
          oldSeat.fill('#DEDEDE')
          oldSeat.stroke('#999999')
          this.$delete(this.seatOccupancy, oldId)
        }
      }

      const oldFigureOnTable = this.figureLayer.findOne(`#${this.draggedGuestUid}`)
      if (oldFigureOnTable) {
        oldFigureOnTable.destroy()
      }

      const tempFigure = new Konva.Circle({
        x: this.stage.getPointerPosition().x,
        y: this.stage.getPointerPosition().y,
        radius: this.seatVisualRadius * 1.5,
        fill: this.getGuestColor(guest.uid),
        stroke: 'black',
        strokeWidth: 2,
        draggable: true,
        id: this.draggedGuestUid,
        name: 'guest-figure-temp',
        guestUid: guest.uid,
        totalSeatsNeeded: totalSeatsNeeded
      })

      this.figureLayer.add(tempFigure)
      this.figureLayer.draw()

      tempFigure.startDrag()
      tempFigure.on('dragend', (e) => this.checkDropCollision(e.target))
    },

    restoreSidebarVisibility (guestUid) {
      const groupElements = document.querySelectorAll(`.guest-group-entry div.guest-figure[data-group-uid="${guestUid}"]`)
      groupElements.forEach(el => {
        el.style.opacity = '1'
      })
    },

    findConsecutiveSeats (tableSeats, targetIndex, seatsRequired, figureId, direction = 1) {
      const totalSeats = tableSeats.length
      const availableSeats = []
      let hasCollision = false

      for (let i = 0; i < seatsRequired; i++) {
        let seatIndex

        if (direction === 1) {
          seatIndex = (targetIndex + i) % totalSeats
        } else {
          seatIndex = (targetIndex - i) % totalSeats
          if (seatIndex < 0) {
            seatIndex += totalSeats
          }
        }

        const currentSeat = tableSeats[seatIndex]
        const currentSeatId = currentSeat.id()

        if (this.seatOccupancy[currentSeatId] && this.seatOccupancy[currentSeatId] !== figureId) {
          hasCollision = true
          break
        }
        availableSeats.push(currentSeat)
      }

      if (hasCollision || availableSeats.length < seatsRequired) {
        return null
      }

      if (direction === -1) {
        availableSeats.reverse()
      }

      return availableSeats
    },

    checkDropCollision (draggedFigure) {
      const guestUid = draggedFigure.getAttr('guestUid')
      const figureId = 'guest-figure-' + guestUid
      const figureAbsPos = draggedFigure.getAbsolutePosition()
      const seats = this.tableLayer.find('.seat')
      const seatsRequired = draggedFigure.getAttr('totalSeatsNeeded') || 1

      let targetSeat = null
      const DROP_RADIUS = draggedFigure.radius() + this.seatVisualRadius

      for (const seat of seats) {
        const seatAbsPos = seat.getAbsolutePosition()
        const dx = figureAbsPos.x - seatAbsPos.x
        const dy = figureAbsPos.y - seatAbsPos.y
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (distance <= DROP_RADIUS) {
          targetSeat = seat
          break
        }
      }

      if (targetSeat) {
        const tableGroup = targetSeat.getParent()
        const tableSeats = tableGroup.find('.seat')
        const targetIndex = tableSeats.findIndex(s => s.id() === targetSeat.id())
        const totalSeats = tableSeats.length

        let availableSeats = null

        if (seatsRequired > totalSeats) {
          alert('Attenzione! Questo tavolo ha solo ' + totalSeats + ' posti, insufficienti per ' + seatsRequired + ' ospiti.')
        } else {
          availableSeats = this.findConsecutiveSeats(tableSeats, targetIndex, seatsRequired, figureId, 1)

          if (!availableSeats) {
            availableSeats = this.findConsecutiveSeats(tableSeats, targetIndex, seatsRequired, figureId, -1)
          }
        }

        if (!availableSeats) {
          draggedFigure.remove()
          this.restoreSidebarVisibility(guestUid)
          this.figureLayer.draw()
          this.tableLayer.draw()
          if (seatsRequired <= totalSeats) {
            alert('Impossibile posizionare il gruppo: i posti consecutivi richiesti sono occupati o non disponibili.')
          }
          return
        }

        for (const seat of availableSeats) {
          const seatId = seat.id()
          seat.fill(this.GUEST_KONVA_COLOR)
          seat.stroke('#4d94ff')
          this.$set(this.seatOccupancy, seatId, figureId)
        }

        const guestObject = this.guests.find(g => g.uid === guestUid)
        const firstSeat = availableSeats[0]

        const firstSeatAbsPos = tableGroup.getAbsoluteTransform().point({
          x: firstSeat.x(),
          y: firstSeat.y()
        })

        const permanentFigure = new Konva.Circle({
          radius: this.seatVisualRadius,
          fill: this.getGuestColor(guestUid),
          stroke: 'black',
          strokeWidth: 2,
          id: figureId,
          name: 'guest-figure',
          guestUid: guestUid,
          draggable: true,
          x: firstSeatAbsPos.x,
          y: firstSeatAbsPos.y
        })

        this.figureLayer.add(permanentFigure)

        permanentFigure.on('dragstart', (e) => this.dragStartGroup(guestObject))
        permanentFigure.on('dragend', (e) => this.checkDropCollision(e.target))

        draggedFigure.remove()
      } else {
        draggedFigure.remove()
        this.restoreSidebarVisibility(guestUid)
      }

      this.figureLayer.draw()
      this.tableLayer.draw()
    },

    /**
     * Serializza l'intero Stage Konva in JSON e lo salva nello store Vuex.
     */
    salvaStage () {
      this.transformer.nodes([])
      this.tableLayer.draw()

      this.stage.width(this.stageWidth)
      this.stage.height(this.stageHeight)

      const stageJSON = this.stage.toJSON()
      const konvaJson = JSON.parse(stageJSON)

      // Salva lo schema Konva aggiornato nello store Vuex
      konovaschema.dispatch('aggiornakonova', konvaJson)

      alert('Disposizione salvata in Vuex (pronta per l\'invio al backend)!')
    },

    resetAll () {
      this.transformer.nodes([])
      this.tableLayer.destroyChildren()
      this.figureLayer.destroyChildren()

      if (this.tableLayer) {
        this.transformer = new Konva.Transformer({
          rotateEnabled: true,
          borderStroke: 'blue',
          anchorFill: 'blue',
          anchorStroke: 'blue',
          enabledAnchors: []
        })
        this.tableLayer.add(this.transformer)
      }

      this.tableLayer.draw()
      this.figureLayer.draw()

      this.seatOccupancy = {}
      this.tableCounter = 0
      this.seats = null
      this.oldSeatId = null

      this.guests.forEach(g => {
        this.restoreSidebarVisibility(g.uid)
      })
    }
  }
}
</script>

<style scoped>
/* Stili scope per il TableManager */

select {
  display: block !important;
}

.corpo-pagina {
    padding: 20px;
    height: 95vh;
    display: flex;
    flex-direction: column;
}

.table-manager-container {
  display: flex;
  position: relative;
  flex-grow: 1;
  overflow: auto;
  padding-bottom: 20px;
}

.controls {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  padding: 10px;
  border-bottom: 1px solid #ccc;
  background-color: #f7f7f7;
  border-radius: 5px;
  flex-shrink: 0;
}

.col {
    flex-grow: 1;
}

.form-control {
  padding: 10px;
  border-radius: 5px;
  font-size: 16px;
  border: 1px solid #ccc;
  width: 100%;
}

.reset-btn {
  background-color: #f44336;
  color: white;
  cursor: pointer;
}

.save-btn {
  background-color: #4CAF50;
  color: white;
  cursor: pointer;
}

.guest-list-sidebar {
  width: 250px;
  padding: 15px;
  background-color: #fff;
  border: 1px solid #ddd;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  max-height: 100%;
  overflow-y: auto;
  z-index: 5;
  margin-right: 20px;
}

.guest-group-entry {
    margin-bottom: 5px;
}

.guest-figure {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-radius: 5px;
  color: white;
  cursor: grab;
  transition: opacity 0.3s, box-shadow 0.1s;
  user-select: none;
  font-size: 14px;
}

.guest-figure.main-guest {
    font-weight: bold;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    margin-bottom: 10px;
    border: 1px solid rgba(255, 255, 255, 0.5);
}

.dimension-accompanying {
    font-size: 12px;
    padding: 6px 8px;
    margin-left: 20px;
    opacity: 0.9;
    font-style: italic;
    font-weight: normal;
}

.accompanying-count {
    font-weight: normal;
    background-color: rgba(255, 255, 255, 0.3);
    padding: 2px 6px;
    border-radius: 3px;
    margin-left: 10px;
}

#container-wrapper {
  position: relative;
  border: 2px dashed #333;

  margin-left: 20px;
  min-width: 300px;
  min-height: 300px;
  flex-shrink: 0;
  /* Rimosse le proprietà statiche dal CSS, ora gestite da dynamicBackgroundStyle */
  /* background-image: url('~@/assets/img/table/modern.png'); */
}

#container {
  width: 100%;
  height: 100%;
}

.resizable-handle {
  position: absolute;
  bottom: -10px;
  right: -10px;
  width: 20px;
  height: 20px;
  background: #333;
  border: 3px solid white;
  border-radius: 4px;
  cursor: nwse-resize;
  z-index: 20;
}
</style>
